import { IDictationItem } from 'common/modules/ReactShared';
import { submitSpeechAnswer } from 'common/modules/ReactShared/data/api/lessonTask/api';
import { ISpeechItemSessionResponse } from 'common/modules/ReactShared/data/api/lessonTask/types';
import { t } from 'i18n';
import { useCallback, useEffect, useRef, useState } from 'react';

export enum SpeechItemState {
  IDLE = 'idle',
  EVALUATING = 'evaluating',
  RESULTS_PASS = 'results_pass',
  RESULTS_FAIL = 'results_fail',
  ERROR = 'error'
}

interface IProps {
  item: IDictationItem;
  itemSessionUrl?: string;
  onItemCompleted: (itemId: number, mistakesCount: number) => void;
  onSkip: (itemId: number) => void;
}

interface IReturnedValue {
  state: SpeechItemState;
  speechResponse: ISpeechItemSessionResponse | null;
  errorMessage: string | null;
  handleSubmitAudio: (blob: Blob) => void;
  handleTryAgain: () => void;
  handleSkip: () => void;
  handleRetryError: () => void;
  handleNext: () => void;
}

export default function useDictationSpeechTaskItem({
  item,
  itemSessionUrl,
  onItemCompleted,
  onSkip
}: IProps): IReturnedValue {
  const [state, setState] = useState<SpeechItemState>(SpeechItemState.IDLE);
  const [speechResponse, setSpeechResponse] = useState<ISpeechItemSessionResponse | null>(
    null
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const lastBlobRef = useRef<Blob | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const submitAudio = useCallback(
    async (blob: Blob) => {
      const evaluationErrorMessage = t(
        'frontend.lesson_task.tasks.play.dictation.speech.evaluation_unavailable',
        { defaultValue: 'Speech evaluation is unavailable. Please try again.' }
      );

      if (!itemSessionUrl) {
        setErrorMessage(evaluationErrorMessage);
        setState(SpeechItemState.ERROR);
        return;
      }

      setState(SpeechItemState.EVALUATING);
      try {
        const response = await submitSpeechAnswer(itemSessionUrl, blob);
        if (!mountedRef.current) return;
        setSpeechResponse(response);

        if (response.correct) {
          setState(SpeechItemState.RESULTS_PASS);
        } else {
          setState(SpeechItemState.RESULTS_FAIL);
        }
      } catch {
        if (!mountedRef.current) return;
        setErrorMessage(evaluationErrorMessage);
        setState(SpeechItemState.ERROR);
      }
    },
    [itemSessionUrl]
  );

  const handleSubmitAudio = useCallback(
    (blob: Blob) => {
      lastBlobRef.current = blob;
      submitAudio(blob);
    },
    [submitAudio]
  );

  const handleTryAgain = useCallback(() => {
    setSpeechResponse(null);
    setState(SpeechItemState.IDLE);
  }, []);

  const handleSkip = useCallback(() => {
    onSkip(item.id);
  }, [item.id, onSkip]);

  const handleRetryError = useCallback(() => {
    if (lastBlobRef.current) {
      submitAudio(lastBlobRef.current);
    } else {
      setState(SpeechItemState.IDLE);
    }
  }, [submitAudio]);

  const handleNext = useCallback(() => {
    if (speechResponse) {
      onItemCompleted(item.id, speechResponse.mistakesCount);
    }
  }, [speechResponse, item.id, onItemCompleted]);

  return {
    state,
    speechResponse,
    errorMessage,
    handleSubmitAudio,
    handleTryAgain,
    handleSkip,
    handleRetryError,
    handleNext
  };
}
